import manageFreeItem from 'lib/shopify/manageFreeItem';
import fetchJson from 'lib/utils/fetchJson';
import announce from 'lib/utils/announce';
import { mutate } from 'swr';
import { useSettings } from 'contexts/SettingContext';
import { useRouter } from 'next/router';
import { getCheckoutIdByLocale } from 'lib/shopify';
import { useUI } from 'hooks';
import { useGlobalModules } from 'contexts';
import { useCart } from './useCart';

export function useAddItemsToCart() {
  const { isFreeItemDisabled } = useSettings();
  const { locale, pathname } = useRouter();
  const { openCartPopup } = useUI();
  const { cart } = useCart();
  const globalModules = useGlobalModules();
  const { shopifyTagsToExcludeFromPromo, amountOfProductsToGetFreeItem, promoCollectionName } =
    globalModules.settings.shopify;
  const { freeItem } = globalModules;

  async function addItems(itemsToAdd) {
    const checkoutId = getCheckoutIdByLocale(locale);
    let error = null;

    const freeVariantsInCart = new Set(
      (cart?.lines?.edges || [])
        .filter(
          ({ node }) =>
            node?.merchandise?.product?.tags?.includes('free-item') ||
            node?.attributes?.some(a => a.key === '_freeItem')
        )
        .map(({ node }) => node?.merchandise?.id)
    );
    const items = itemsToAdd.filter(item => !freeVariantsInCart.has(item.variantId));
    if (!items.length) return null;

    items.forEach(item => {
      if (item.variantId == null) {
        error = `Missing variantId in item`;
      }

      if (item.quantity == null) {
        error = `Missing quantity in item with variant id: ${item.variantId}`;
      } else if (typeof item.quantity !== 'number') {
        error = `Quantity is not a number in item with variant id: ${item.variantId}`;
      } else if (item.quantity < 1) {
        error = `Quantity must not be less than one in item with variant id: ${item.variantId}`;
      }
    });

    if (error) {
      console.log(error);
      return null;
    }

    async function addLineItems(itemsArray) {
      const data = await fetchJson('/api/shopify/checkout/add-items/', {
        method: 'POST',
        body: JSON.stringify({
          checkoutId,
          lineItems: itemsArray,
          locale,
        }),
      });

      await mutate('/api/shopify/checkout/request/', { ...data, locale }, false);

      const lines = data?.data?.lines?.edges || [];
      const addedTitles = [
        ...new Set(
          itemsArray
            .map(
              item =>
                lines.find(({ node }) => node?.merchandise?.id === item.variantId)?.node
                  ?.merchandise?.product?.title
            )
            .filter(Boolean)
        ),
      ];
      const addedToCartText =
        globalModules?.translations?.accessibility?.addedToCart || 'added to cart';

      if (!isFreeItemDisabled) {
        await manageFreeItem(
          data,
          freeItem?.shopifyData,
          { shopifyTagsToExcludeFromPromo, amountOfProductsToGetFreeItem, promoCollectionName },
          locale
        );
      }
      if (pathname !== '/cart') openCartPopup();
      announce(
        addedTitles.length ? `${addedTitles.join(', ')} ${addedToCartText}` : addedToCartText
      );
      return data;
    }

    return addLineItems(items);
  }

  return addItems;
}

export default useAddItemsToCart;
